import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import deepEqual from 'fast-deep-equal';
import { Heading2, Heading3 } from '../../ui/Typography/Heading';
import { Input, Select, Button, Tooltip } from '../../ui';
import { IconTrash, IconCircledPlus, IconInfo } from '../../ui/Icons';
import { updateConfigOnboardingSettings } from '../../../redux/config/slice';
import { getOrganizations } from '../../../redux/organizations/slice';
import { getUsers, getUserRoles, bulkEditUsers } from '../../../redux/users/slice';
import { OnboardingNavbar } from '../OnboardingNavbar/OnboardingNavbar';
import { OnboardingProgress } from '../OnboardingProgress/OnboardingProgress';
import { OnboardingActions } from '../OnboardingActions/OnboardingActions';
import ConfirmLeaveModal from '../../ConfirmLeaveModal/ConfirmLeaveModal';
import {
  selectUpdateConfigLoading,
  selectConfigOnboardingImportSource,
  selectConfigOnboardingImportDone
} from '../../../redux/config/selectors';
import { selectOrganizationsForOnboarding, selectOrganizationsLoading } from '../../../redux/organizations/selectors';
import { selectUsers, selectUserRoleOptions, selectBulkEditUsersLoading } from '../../../redux/users/selectors';
import { ONBOARDING_STEPS, ONBOARDING_SOURCE_TYPES, ORGANIZATIONS_TYPES } from '../../../consts/consts';
import { isHiddenOqioUser } from '../../../helpers/utility';
import {
  OnboardingHeaderGroup,
  OnboardingContent,
  OnboardingContentGroup,
  OnboardingContentTitle,
  OnboardingStepWrapper,
  OnboardingContentInfo,
  OnboardingContentGroupText,
  OnboardingContentGroupLink,
  OnboardingFieldWrapper,
  OnboardingFieldErrorText,
  OnboardingUserListRow,
  OnboardingUserListHeader,
  OnboardingUserListHeaderItem,
  OnboardingNotificationEmptyState,
  OnboardingInsuranceInfoIcon
} from '../../ui/Layout/OnboardingLayout';
import {
  EMAIL_FORMAT_REGEX,
  normalizeEmail,
  trimValue,
  getFieldTouchKey,
  touchAllFields
} from '../../../helpers/onboardingValidation.js';

const ONBOARDING_HELP_CENTER_URL =
  'https://help.oqio.de/de/articles/13634593-rollen-und-rechtemanagement?utm_source=onboarding&utm_medium=inproduct';

const Separator = styled.div`
  border-bottom: 1px solid var(--greyscale-dark-white);
`;

export const OnboardingStepOrganizationsUsersComponent = ({
  updateConfigOnboardingSettings,
  updateConfigLoading,
  sourceSelected,
  importDone,
  organizations,
  organizationsLoading,
  users,
  userRoleOptions,
  getOrganizations,
  getUsers,
  getUserRoles,
  bulkEditUsers,
  bulkEditUsersLoading
}) => {
  const [localUsers, setLocalUsers] = useState([]);
  const [initialUsersState, setInitialUsersState] = useState([]);
  const [touchedFields, setTouchedFields] = useState({});
  const [userIdPendingDelete, setUserIdPendingDelete] = useState(null);

  useEffect(() => {
    getOrganizations();
    getUsers({});

    // TODO wir holen die Roles nur einmal weil sie für alle gleich sind
    // call einfach machen ohne logik - kommentar schreiben
    const firstCreatableOrganizationType = ORGANIZATIONS_TYPES.find((orgType) => orgType.canBeCreated);

    if (firstCreatableOrganizationType) {
      getUserRoles({ organisationType: firstCreatableOrganizationType.value });
    }
  }, [getOrganizations, getUsers, getUserRoles]);

  useEffect(() => {
    // Only initialize if we have organizations and users loaded, to avoid wiping state or partial loads
    if (organizations.length > 0 && users) {
      const orgIds = organizations.map((o) => o.id);
      const filteredExistingUsers = users
        .filter((user) => !isHiddenOqioUser(user))
        .filter((user) => orgIds.includes(user.organization_id));

      setLocalUsers(filteredExistingUsers);
      setInitialUsersState(filteredExistingUsers);
    }
  }, [users, organizations]);

  const isLoading = updateConfigLoading || organizationsLoading || bulkEditUsersLoading;

  const isFieldTouched = (userId, fieldName) => touchedFields[getFieldTouchKey(userId, fieldName)];

  const handleFieldBlur = (userId, fieldName) => {
    setTouchedFields((prev) => ({
      ...prev,
      [getFieldTouchKey(userId, fieldName)]: true
    }));
  };

  // Returns true if another user in the list shares the same email.
  const isEmailDuplicated = (email) => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return false;
    return localUsers.filter((u) => normalizeEmail(u.email) === normalizedEmail).length > 1;
  };

  const getUserFieldErrorMessage = (user, fieldName, requireTouched = true) => {
    if (requireTouched && !isFieldTouched(user.id, fieldName)) {
      return '';
    }

    const trimmedFirstName = trimValue(user.first_name);
    const trimmedLastName = trimValue(user.last_name);
    const trimmedEmail = trimValue(user.email);
    const normalizedUserEmail = normalizeEmail(user.email);

    if (fieldName === 'first_name' && !trimmedFirstName) {
      return '↑ Pflichtfeld';
    }

    if (fieldName === 'last_name' && !trimmedLastName) {
      return '↑ Pflichtfeld';
    }

    if (fieldName === 'role' && !user.role) {
      return '↑ Pflichtfeld';
    }

    if (fieldName === 'email') {
      if (!trimmedEmail) {
        return '↑ Pflichtfeld';
      }

      if (!EMAIL_FORMAT_REGEX.test(normalizedUserEmail)) {
        return '↑ Die Eingabe ist keine gültige E-Mail';
      }

      if (isEmailDuplicated(user.email)) {
        return '↑ Diese E-Mail-Adresse wird bereits verwendet';
      }

      if (isHiddenOqioUser({ ...user, email: normalizedUserEmail })) {
        return '↑ Diese E-Mail-Adresse kann nicht verwendet werden';
      }
    }

    return '';
  };

  const isUserRowEmpty = (user) =>
    !trimValue(user.first_name) && !trimValue(user.last_name) && !trimValue(user.email) && !user.role;

  const isUserValid = (user) => {
    return ['first_name', 'last_name', 'email', 'role'].every(
      (fieldName) => !getUserFieldErrorMessage(user, fieldName, false)
    );
  };

  const handleAddUser = (orgId) => {
    const newUser = {
      id: `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      first_name: '',
      last_name: '',
      email: '',
      role: null,
      organization_id: orgId,
      isNew: true
    };
    // Insert at the top of the organization's section (while keeping a single flat array)
    setLocalUsers((prev) => {
      const firstUserFromSameOrgIndex = prev.findIndex((u) => u.organization_id === orgId);
      if (firstUserFromSameOrgIndex === -1) return [...prev, newUser];
      return [...prev.slice(0, firstUserFromSameOrgIndex), newUser, ...prev.slice(firstUserFromSameOrgIndex)];
    });
  };

  const handleUpdateLocalUser = (userId, field, value) => {
    setLocalUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, [field]: value } : u)));
  };

  const handleDeleteLocalUser = (userId) => {
    setLocalUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const handleOnDeleteUserClick = (userId) => {
    const userToDelete = localUsers.find((u) => u.id === userId);

    // Newly added (not yet persisted) users can be removed immediately.
    if (userToDelete?.isNew) {
      handleDeleteLocalUser(userId);
      return;
    }

    // Existing DB users require confirmation.
    setUserIdPendingDelete(userId);
  };

  const handleCancelDeleteUser = () => setUserIdPendingDelete(null);

  const handleConfirmDeleteUser = () => {
    if (userIdPendingDelete) {
      handleDeleteLocalUser(userIdPendingDelete);
    }
    setUserIdPendingDelete(null);
  };

  const processUsersAndSave = (callback) => {
    // Drop empty new rows before validating
    const filteredUsers = localUsers.filter((user) => !(user.isNew && isUserRowEmpty(user)));
    setLocalUsers(filteredUsers);

    if (filteredUsers.some((user) => !isUserValid(user))) {
      setTouchedFields(touchAllFields(filteredUsers, ['first_name', 'last_name', 'email', 'role']));
      return;
    }

    const deletedUsers = initialUsersState.filter(
      (initUser) => !filteredUsers.find((locUser) => locUser.id === initUser.id)
    );

    const usersToProcess = filteredUsers
      .map((user) => {
        if (user.isNew) return user;

        const initialUser = initialUsersState.find((u) => u.id === user.id);
        if (initialUser && !deepEqual(user, initialUser)) {
          return { ...user, isModified: true };
        }
        return null;
      })
      .filter(Boolean);

    if (deletedUsers.length === 0 && usersToProcess.length === 0) {
      callback?.();
      return;
    }

    bulkEditUsers({
      usersToProcess,
      deletedUsers,
      callback
    });
  };

  const getPreviousStep = () => {
    if (sourceSelected === ONBOARDING_SOURCE_TYPES.API) {
      return ONBOARDING_STEPS.API_INFO;
    } else if (sourceSelected === ONBOARDING_SOURCE_TYPES.IMPORT) {
      return ONBOARDING_STEPS.IMPORT_UPLOAD;
    } else if (importDone) {
      return ONBOARDING_STEPS.IMPORT_UPLOAD;
    } else {
      return ONBOARDING_STEPS.DATA_SOURCE_SELECT;
    }
  };

  const handleBack = () => {
    processUsersAndSave(() => updateConfigOnboardingSettings({ step: getPreviousStep() }));
  };

  const handleNext = () => {
    processUsersAndSave(() => updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.HOUSING_STOCK_SUCCESS }));
  };

  const getUsersForOrganization = (orgId) => {
    return localUsers.filter((user) => user.organization_id === orgId) || [];
  };

  return (
    <>
      {userIdPendingDelete && (
        <ConfirmLeaveModal
          title="Nutzer löschen"
          message="Wollen Sie diesen Nutzer wirklich löschen?"
          cancelButtonLabel="Abbrechen"
          okButtonLabel="Nutzer löschen"
          onCancelButtonClick={handleCancelDeleteUser}
          onOkButtonClick={handleConfirmDeleteUser}
          open
        />
      )}
      <OnboardingNavbar loading={isLoading} />
      <OnboardingProgress />
      <OnboardingContent>
        <OnboardingStepWrapper className="step-users">
          <OnboardingHeaderGroup>
            <OnboardingContentTitle>
              <Heading2>Kundenmanagement auf oqio</Heading2>
            </OnboardingContentTitle>
            <OnboardingContentInfo>
              <OnboardingContentGroupText>
                Bestimmen Sie, wie Sie mit den jeweiligen Unternehmen zusammenarbeiten und verwalten Sie die Befugnisse.
                Unterscheiden Sie zwischen den Rollen Sachbearbeiter und Manager.
              </OnboardingContentGroupText>
              <OnboardingContentGroupText>
                Registrierte Nutzer erhalten umgehend eine E-Mail für die persönliche Anmeldung in Ihrem Portal.
              </OnboardingContentGroupText>
              <OnboardingContentGroupLink href={ONBOARDING_HELP_CENTER_URL} target="_blank" rel="noopener noreferrer">
                Zur Rollen- und Rechteübersicht
              </OnboardingContentGroupLink>
            </OnboardingContentInfo>
          </OnboardingHeaderGroup>

          {organizations && organizations.length > 0 ? (
            organizations.map((org, index) => {
              const orgUsers = getUsersForOrganization(org.id);
              return (
                <React.Fragment key={org.id}>
                  {index > 0 && <Separator />}
                  <OnboardingContentGroup className="step-users action">
                    <Heading3>{org.name}</Heading3>
                    {org.type === 'insurance' ? (
                      <Tooltip
                        placement="left"
                        mouseEnterDelay={0.1}
                        title="Für Versicherungen sind keine Nutzerzugänge vorgesehen."
                      >
                        <OnboardingInsuranceInfoIcon>
                          <IconInfo />
                        </OnboardingInsuranceInfoIcon>
                      </Tooltip>
                    ) : (
                      <Button type="link" onClick={() => handleAddUser(org.id)} icon={<IconCircledPlus />}>
                        Hinzufügen
                      </Button>
                    )}
                  </OnboardingContentGroup>

                  {orgUsers.length > 0 && (
                    <>
                      <OnboardingContentGroup className="step-users content-head">
                        <OnboardingUserListHeader>
                          <OnboardingUserListHeaderItem>Vorname</OnboardingUserListHeaderItem>
                          <OnboardingUserListHeaderItem>Nachname</OnboardingUserListHeaderItem>
                          <OnboardingUserListHeaderItem>E-Mail</OnboardingUserListHeaderItem>
                          <OnboardingUserListHeaderItem>Rolle</OnboardingUserListHeaderItem>
                          <OnboardingUserListHeaderItem></OnboardingUserListHeaderItem>
                        </OnboardingUserListHeader>
                      </OnboardingContentGroup>

                      <OnboardingContentGroup className="step-organization-users content">
                        {orgUsers.map((user) => {
                          const firstNameError = getUserFieldErrorMessage(user, 'first_name');
                          const lastNameError = getUserFieldErrorMessage(user, 'last_name');
                          const emailError = getUserFieldErrorMessage(user, 'email');
                          const roleError = getUserFieldErrorMessage(user, 'role');

                          return (
                            <OnboardingUserListRow key={user.id}>
                              <OnboardingFieldWrapper>
                                <Input
                                  value={user.first_name}
                                  onChange={(e) => handleUpdateLocalUser(user.id, 'first_name', e.target.value)}
                                  onBlur={() => handleFieldBlur(user.id, 'first_name')}
                                  status={firstNameError ? 'error' : undefined}
                                />
                                {firstNameError && (
                                  <OnboardingFieldErrorText>{firstNameError}</OnboardingFieldErrorText>
                                )}
                              </OnboardingFieldWrapper>
                              <OnboardingFieldWrapper>
                                <Input
                                  value={user.last_name}
                                  onChange={(e) => handleUpdateLocalUser(user.id, 'last_name', e.target.value)}
                                  onBlur={() => handleFieldBlur(user.id, 'last_name')}
                                  status={lastNameError ? 'error' : undefined}
                                />
                                {lastNameError && <OnboardingFieldErrorText>{lastNameError}</OnboardingFieldErrorText>}
                              </OnboardingFieldWrapper>
                              <OnboardingFieldWrapper>
                                <Input
                                  value={user.email}
                                  onChange={(e) => handleUpdateLocalUser(user.id, 'email', e.target.value)}
                                  onBlur={() => handleFieldBlur(user.id, 'email')}
                                  status={emailError ? 'error' : undefined}
                                  disabled={!user.isNew}
                                />
                                {emailError && <OnboardingFieldErrorText>{emailError}</OnboardingFieldErrorText>}
                              </OnboardingFieldWrapper>
                              <OnboardingFieldWrapper>
                                <Select
                                  value={user.role}
                                  onChange={(value) => handleUpdateLocalUser(user.id, 'role', value)}
                                  onBlur={() => handleFieldBlur(user.id, 'role')}
                                  options={userRoleOptions || []}
                                  placeholder="Rolle wählen"
                                  showCheckmark={false}
                                  status={roleError ? 'error' : undefined}
                                />
                                {roleError && <OnboardingFieldErrorText>{roleError}</OnboardingFieldErrorText>}
                              </OnboardingFieldWrapper>
                              <Button
                                type="link"
                                danger
                                icon={<IconTrash />}
                                onClick={() => handleOnDeleteUserClick(user.id)}
                              />
                            </OnboardingUserListRow>
                          );
                        })}
                      </OnboardingContentGroup>
                    </>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <OnboardingContentGroup className="users content">
              <OnboardingNotificationEmptyState>Keine Unternehmen vorhanden</OnboardingNotificationEmptyState>
            </OnboardingContentGroup>
          )}
        </OnboardingStepWrapper>
        <OnboardingActions className="step-users" onBack={handleBack} onNext={handleNext} loading={isLoading} />
      </OnboardingContent>
    </>
  );
};

const mapStateToProps = (state) => ({
  updateConfigLoading: selectUpdateConfigLoading(state),
  sourceSelected: selectConfigOnboardingImportSource(state),
  importDone: selectConfigOnboardingImportDone(state),
  organizations: selectOrganizationsForOnboarding(state),
  organizationsLoading: selectOrganizationsLoading(state),
  users: selectUsers(state),
  userRoleOptions: selectUserRoleOptions(state),
  bulkEditUsersLoading: selectBulkEditUsersLoading(state)
});

const mapDispatchToProps = {
  updateConfigOnboardingSettings,
  getOrganizations,
  getUsers,
  getUserRoles,
  bulkEditUsers
};

export const OnboardingStepOrganizationsUsers = connect(
  mapStateToProps,
  mapDispatchToProps
)(OnboardingStepOrganizationsUsersComponent);
